import type {
  AudioItem,
  Playlist,
  PlaylistAudioItem,
  PlaylistUpdateAction,
  PopOutAudioState,
} from './types';
import { PlayStates, VisibilityStates } from '../pop-out-media-manager/types';

const getPlaylistItemFromAudio = (item: AudioItem): PlaylistAudioItem => ({
  ...item,
  state: {
    playState: PlayStates.Paused,
    active: false,
  },
});

const isAudioInState = (id: string, idsInState: Array<string>): boolean => {
  return idsInState.includes(id);
};

const addSingleItemToPlaylist = (
  state: PopOutAudioState,
  audio: AudioItem,
): PopOutAudioState => {
  return {
    ...state,
    playlist: [...state.playlist, getPlaylistItemFromAudio(audio)],
  };
};

const addOnlyNewAudios = (
  audioToAdd: AudioItem | Array<AudioItem>,
  state: PopOutAudioState,
): PopOutAudioState => {
  const audioIdsInState = state.playlist.map(audio => audio.itemId);
  if (Array.isArray(audioToAdd)) {
    const newAudios = audioToAdd.reduce<AudioItem[]>((newArr, audio) => {
      if (!isAudioInState(audio.itemId, audioIdsInState)) {
        newArr.push(audio);
      }
      return newArr;
    }, []);
    return {
      ...state,
      playlist: [...state.playlist, ...newAudios.map(getPlaylistItemFromAudio)],
    };
  }

  if (!isAudioInState(audioToAdd.itemId, audioIdsInState)) {
    return addSingleItemToPlaylist(state, audioToAdd);
  }

  return state;
};

const setAudioStatePlaying = (
  itemId: string,
  state: PopOutAudioState,
): PopOutAudioState => {
  if (!itemId) {
    return state;
  }
  return {
    ...state,
    visibilityState: VisibilityStates.Visible,
    playlist: [
      ...state.playlist.map<PlaylistAudioItem>(playlistItem => {
        const shouldPlayItem = playlistItem.itemId === itemId;
        return {
          ...playlistItem,
          state: {
            ...playlistItem.state,
            playState: shouldPlayItem ? PlayStates.Playing : PlayStates.Paused,
            active: shouldPlayItem,
          },
        };
      }),
    ],
  };
};

const setAudioStatePaused = (
  itemId: string,
  state: PopOutAudioState,
): PopOutAudioState => {
  if (!itemId) {
    return state;
  }
  return {
    ...state,
    playlist: [
      ...state.playlist.map(playlistItem => {
        if (playlistItem.itemId === itemId) {
          return {
            ...playlistItem,
            state: {
              ...playlistItem.state,
              playState: PlayStates.Paused,
            },
          };
        }
        return playlistItem;
      }),
    ],
  };
};

const setAllAudiosInactive = (state: PopOutAudioState): PopOutAudioState => {
  return {
    ...state,
    playlist: [
      ...state.playlist.map(playlistItem => ({
        ...playlistItem,
        state: {
          ...playlistItem.state,
          playState: PlayStates.Paused,
          active: false,
        },
      })),
    ],
  };
};

export const getAudioFromPlaylist = (
  playlist: Playlist,
  id: string,
): PlaylistAudioItem | null => {
  return playlist.find(item => id === item.itemId) || null;
};

export const getNextAudio = (
  playlist: Playlist,
  currentId: string,
): string | null => {
  for (let i = 0; i < playlist.length; i++) {
    const playlistItem = playlist[i];
    if (playlistItem?.itemId === currentId) {
      const nextUp = playlist[i + 1];
      return nextUp ? nextUp.itemId : null;
    }
  }
  return null;
};

export const getPreviousAudio = (
  playlist: Playlist,
  currentId: string,
): string | null => {
  for (let i = 0; i < playlist.length; i++) {
    const playlistItem = playlist[i];
    if (playlistItem?.itemId === currentId) {
      const previous = playlist[i - 1];
      return previous ? previous.itemId : null;
    }
  }
  return null;
};

export const PlaylistStateReducer = (
  state: PopOutAudioState,
  action: PlaylistUpdateAction,
): PopOutAudioState => {
  switch (action.type) {
    case 'ADD_AUDIO':
      return addOnlyNewAudios(action.payload, state);
    case 'PLAY_AUDIO':
      return setAudioStatePlaying(action.payload.itemId, state);
    case 'PAUSE_AUDIO':
      return setAudioStatePaused(action.payload.itemId, state);
    case 'SET_ALL_INACTIVE':
      return setAllAudiosInactive(state);
    case 'CLEAR_AUDIOS':
      return {
        ...state,
        playlist: [],
      };
    case 'SET_POP_OUT_VISIBILITY_STATE':
      return {
        ...state,
        visibilityState: action.payload,
      };
    default:
      return state;
  }
};
